<template>
  <div class="screen-time-management-mode-picker">
    <div class="main-title">
      {{ $t("screentimeManagement.whenScreentimeOut") }}
    </div>

    <div class="mode-selector"
         :class="{ 'open': isModesDropdownOpen }"
         ref="modeSelector">
      <span class="mode-selector-text">
        <template v-if="isModeNoInternet(selectedMode.name)">
          {{ $t("deviceAvailability.noInternet") }}
        </template>

        <template v-if="isModePauseDevice(selectedMode.name)">
          {{ $t("deviceAvailability.pauseDevice") }}
        </template>
      </span>

      <icon-arrow-down class="mode-selector-caret"
                       :class="{ 'open': isModesDropdownOpen }"
                       width="16"
                       height="16"/>
    </div>

    <div class="modes-dropdown"
         ref="modesDropdown">
      <ul class="modes-list">
        <li class="modes-option"
            v-for="mode in modes"
            :key="mode.id">
          <base-radio class="modes-radio-button"
                      :value="selectedMode.id"
                      :true-value="mode.id"
                      :disabled="disabled"
                      @change="setNewMode">
            
            <template v-if="isModeNoInternet(mode.name)">
              {{ $t("deviceAvailability.noInternet") }}
            </template>

            <template v-if="isModePauseDevice(mode.name)">
              {{ $t("deviceAvailability.pauseDevice") }}
            </template>

          </base-radio>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
  import i18n from '@/services/i18n'
  import tippy from 'tippy.js'
  import analytics from '@/services/analytics.js'

  export default {
    props: {
      child: {
        type: Object,
        required: true
      },

      disabled: {
        type: Boolean,
        default: false,
        required: false
      }
    },

    data() {
      return {
        isModesDropdownOpen: false,
        isChangingMode: false,
      }
    },

    computed: {
      selectedMode() {
        return this.modes.find(mode => mode.id === this.child.settings.screen_time_exhausted_mode_id)
      },

      modes() {
        return this.child.modes.filter(mode => mode.name === 'No Internet' || mode.name === 'Pause Device')
      },

      pauseModeId() {
        let pauseMode = this.child.modes.find(mode => mode.name === 'Pause Device')

        return pauseMode.id
      }
    },

    mounted() {
      this.initModesDropdown()
    },

    methods: {
      setNewMode(modeId) {
        if (modeId === this.pauseModeId) {
          analytics.sendEvent('St_PauseType_ScrTimeManage')
        }

        this.isChangingMode = true

        this.updateChildSettings({
          child_id: this.child.id,
          data: {
            screen_time_exhausted_mode_id: modeId
          }
        }).then(() => {
          this.isChangingMode = false

          this.showSuccessNotification()
        }).catch(() => {
          this.isChangingMode = false

          this.showFailNotification()
        })

        this.$refs.modeSelector._tippy.hide()
      },

      showSuccessNotification() {
        this.$notify({
          type: 'success',
          text: i18n.t("screentimeManagement.successSaveExceededSetting")
        })
      },

      showFailNotification() {
        this.$notify({
          type: 'error',
          text: i18n.t("screentimeManagement.errorSaveExceededSetting")
        })
      },

      initModesDropdown() {
        const vueComponent = this

        tippy(this.$refs.modeSelector,  {
          allowHTML: true,
          content: this.$refs.modesDropdown,
          trigger: 'click',
          hideOnClick: true,
          interactive: true,
          theme: 'custom-light',
          placement: 'bottom',
          animation: 'shift-away',
          arrow: true,
          inertia: true,
          duration: [400, 300],
          boundary: 'window',
          onShow() {
            vueComponent.isModesDropdownOpen = true
          },
          onHide() {
            vueComponent.isModesDropdownOpen = false
          }
        })
      },

      ...mapActions('children', {
        updateChildSettings: 'updateChildSettings'
      }),

      isModeNoInternet(label) {
        if(label==='No Internet')
        {
          return true;
        }
        else
        {
          return false;
        }
      },

      isModePauseDevice(label) {
        if(label==='Pause Device')
        {
          return true;
        }
        else
        {
          return false;
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .screen-time-management-mode-picker {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 15px 0;
    font-size: 16px;
    color: #3e3a3b;
    border-top: 1px solid #f2f2f2;
    border-bottom: 1px solid #f2f2f2;
  }

  .mode-selector {
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: all .1s ease-out;
    user-select: none;

    &:hover,
    &.open {
      color: map-get($colors, primary);

      .mode-selector-caret {
        color: rgba(map-get($colors, primary), .6);
      }
    }
  }

  .mode-selector-text {
    margin-right: 8px;
    line-height: 1;
  }

  .mode-selector-caret {
    color: #b9b9b9;
    transition: color .1s ease-out, transform .2s ease-out;

    &.open {
      transform: rotate(180deg);
    }
  }

  .modes-list {
    padding: 0;
  }

  .modes-option {
    display: flex;
    align-items: center;
    padding: 0;
    list-style-type: none;
    user-select: none;

    &:not(:last-child) {
      border-bottom: 1px solid #f2f2f2;
    }
  }

  .modes-radio-button {
    width: 100%;
    padding: 12px 16px;
  }
</style>
